import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import settingsApi, {
  UpdateSettingsRequest,
  TestConnectionRequest,
} from "../../api/settings";
import { UserSettings } from "../../types";

interface SettingsState {
  settings: UserSettings | null;
  loading: boolean;
  saving: boolean;
  testing: boolean;
  error: string | null;
  testResult: { success: boolean; message: string } | null;
}

const initialState: SettingsState = {
  settings: null,
  loading: false,
  saving: false,
  testing: false,
  error: null,
  testResult: null,
};

export const fetchSettings = createAsyncThunk(
  "settings/fetch",
  async (_, { rejectWithValue }) => {
    try {
      return await settingsApi.getSettings();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to fetch settings"
      );
    }
  }
);

export const updateSettings = createAsyncThunk(
  "settings/update",
  async (data: UpdateSettingsRequest, { rejectWithValue }) => {
    try {
      return await settingsApi.updateSettings(data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to update settings"
      );
    }
  }
);

export const deleteApiKey = createAsyncThunk(
  "settings/deleteApiKey",
  async (_, { rejectWithValue }) => {
    try {
      await settingsApi.deleteApiKey();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to delete API key"
      );
    }
  }
);

export const testConnection = createAsyncThunk(
  "settings/testConnection",
  async (data: TestConnectionRequest, { rejectWithValue }) => {
    try {
      return await settingsApi.testConnection(data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Connection test failed"
      );
    }
  }
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTestResult: (state) => {
      state.testResult = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch settings
    builder.addCase(fetchSettings.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSettings.fulfilled, (state, action) => {
      state.loading = false;
      state.settings = action.payload;
    });
    builder.addCase(fetchSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // Update settings
    builder.addCase(updateSettings.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(updateSettings.fulfilled, (state, action) => {
      state.saving = false;
      state.settings = action.payload;
    });
    builder.addCase(updateSettings.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });
    // Delete API key
    builder.addCase(deleteApiKey.fulfilled, (state) => {
      if (state.settings) {
        state.settings.has_api_key = false;
      }
    });
    // Test connection
    builder.addCase(testConnection.pending, (state) => {
      state.testing = true;
      state.testResult = null;
    });
    builder.addCase(testConnection.fulfilled, (state, action) => {
      state.testing = false;
      state.testResult = action.payload;
    });
    builder.addCase(testConnection.rejected, (state, action) => {
      state.testing = false;
      state.testResult = {
        success: false,
        message: action.payload as string,
      };
    });
  },
});

export const { clearError, clearTestResult } = settingsSlice.actions;
export default settingsSlice.reducer;
